import { EXTRACTION_FIELD_LABELS, type ExtractionDocumentType, type ExtractionJob } from '../../api/aiExtraction';

const formatValue = (key: string, v: unknown): string => {
  if (typeof v === 'number' && /Vnd$|Kg$/.test(key)) return v.toLocaleString('vi-VN');
  return String(v);
};

/**
 * What AI read off one document: the fields (already filled into the form's
 * empty inputs by the caller), the backend's checks on them, and a way to
 * overwrite inputs that were already filled. A failure never blocks the form —
 * it only says so; every input stays editable.
 */
export function ExtractionResult({
  docType,
  reading,
  job,
  onOverwrite,
}: {
  docType: ExtractionDocumentType;
  reading: boolean;
  job: ExtractionJob | null;
  onOverwrite?: () => void;
}) {
  if (reading) {
    return (
      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-sky-700 animate-pulse">
        <i className="ri-sparkling-2-line" /> AI đang đọc tài liệu để tự điền — bạn vẫn có thể nhập tiếp...
      </p>
    );
  }
  if (!job) return null;
  if (job.status !== 'DONE' || !job.fields) {
    return (
      <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
        <i className="ri-error-warning-line mr-1" />
        {job.errorMessage || 'Không đọc được tài liệu.'} Bạn vẫn có thể tự nhập.
      </p>
    );
  }

  const isMock = job.source === 'MOCK';
  const rows = EXTRACTION_FIELD_LABELS[docType].filter(([key]) => job.fields?.[key] !== undefined && job.fields?.[key] !== '');
  const checks = (job.checks ?? []).filter((c) => c.code !== 'MOCK');

  return (
    <div className={`mt-2 rounded-lg border p-2 ${isMock ? 'border-amber-200 bg-amber-50' : 'border-sky-200 bg-sky-50'}`}>
      <p className={`text-[11px] font-semibold ${isMock ? 'text-amber-800' : 'text-sky-800'}`}>
        <i className="ri-sparkling-2-line mr-1" />
        {isMock ? 'Dữ liệu MẪU — máy chủ chưa cấu hình AI, không đọc từ tệp này' : 'AI đã đọc và tự điền vào các ô còn trống — kiểm tra lại trước khi lưu'}
      </p>
      {rows.length > 0 ? (
        <dl className="mt-1 space-y-0.5 text-[11px]">
          {rows.map(([key, label]) => (
            <div key={key} className="flex gap-1">
              <dt className="shrink-0 text-slate-500">{label}:</dt>
              <dd className="min-w-0 break-words font-medium text-slate-800 whitespace-pre-line">{formatValue(key, job.fields![key])}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="mt-1 text-[11px] text-slate-600">Không tìm thấy thông tin cần thiết trên tài liệu này.</p>
      )}
      {checks.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 border-t border-sky-100 pt-1.5">
          {checks.map((c) => (
            <li key={c.code} className={`flex items-start gap-1 text-[11px] ${c.ok ? 'text-agrida-700' : 'text-amber-700'}`}>
              <i className={`mt-px ${c.ok ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}`} />
              <span>{c.label}</span>
            </li>
          ))}
        </ul>
      )}
      {onOverwrite && rows.length > 0 && (
        <button type="button" onClick={onOverwrite} className="mt-2 h-7 w-full rounded-md border border-sky-300 bg-white text-[11px] font-semibold text-sky-700">
          Ghi đè cả các ô đã nhập bằng dữ liệu này
        </button>
      )}
    </div>
  );
}
